'use client'

/**
 * Tema del **sito** (chiaro/scuro), scelto da chi guarda.
 *
 * Non è una proprietà del tabellone né un comando dell'operatore: è una
 * preferenza del visitatore, vale per tutte le pagine e sopravvive al
 * ricaricamento. Si applica su `<html data-theme>` — dove i token `--league-*`
 * del tema scuro sono già definiti — così cambiano insieme header, pagine e
 * tabellone.
 *
 * Lo script inline in `app/layout.tsx` applica la preferenza **prima del paint**:
 * senza, un visitatore col tema scuro vedrebbe un lampo bianco a ogni pagina.
 */
import { useCallback, useEffect, useState } from 'react'

export type SiteTheme = 'light' | 'dark'

export const SITE_THEME_KEY = 'lv-theme'
/**
 * Evento locale: `data-theme` è globale, ma ogni componente che usa il tema ha il
 * suo stato. Senza notifica, cambiare tema dall'header non ridisegnava il
 * tabellone (che ascolta lo stesso hook in un'altra parte dell'albero).
 */
const THEME_EVENT = 'lv-theme-change'

/** Legge il tema **effettivo** dal documento (già applicato dallo script). */
function currentTheme(): SiteTheme | null {
  const value = document.documentElement.dataset.theme
  return value === 'dark' || value === 'light' ? value : null
}

export function useSiteTheme() {
  const [theme, setThemeState] = useState<SiteTheme>('light')

  useEffect(() => {
    const sync = () => {
      const saved = currentTheme()
      if (saved) setThemeState(saved)
    }
    sync()
    window.addEventListener(THEME_EVENT, sync)
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener(THEME_EVENT, sync)
      window.removeEventListener('storage', sync)
    }
  }, [])

  const setTheme = useCallback((next: SiteTheme) => {
    setThemeState(next)
    document.documentElement.dataset.theme = next
    try {
      window.localStorage.setItem(SITE_THEME_KEY, next)
    } catch {
      /* navigazione privata: vale solo per questa pagina */
    }
    window.dispatchEvent(new Event(THEME_EVENT))
  }, [])

  return { theme, setTheme }
}

export default useSiteTheme
